import React, { useState, useEffect, useMemo } from "react";
import {
  Avatar, Dropdown, Spin, message, ConfigProvider,
  Popover, Modal, Button, Input, AutoComplete, Menu, Breadcrumb,
} from "antd";
import { LoadingOutlined, HomeOutlined } from "@ant-design/icons";
import { fetchDynamicMenuData } from "./menuTransformer";
import { DynamicTabSystem }     from "./rendorTabs";
import { deleteCookie, getCookie } from "../../utilities/utll";
import { menuConfig, profileMenuItems } from "./menuConfig";
import companyLogo  from "../../assets/logo_car.png";
import companyLogo1 from "../../assets/real.svg";
import { APIURL }     from "../../utilities/api";
import { apiRequest } from "../../utilities/apiMethod";
import { ChangePasswordModal } from "../Settings/ChangePassword";
import useTabsSystemStore from "../../store/RenderTabStore";
import { useThemeStore, THEMES } from "../../store/useThemeStore";

/* ── helpers ─────────────────────────────────────────────────────────────── */
const tint = (hex, op = 0.08) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${op})`;
};

/* extract hue + saturation from hex so we can build dark-toned sidebar grads */
const hexToHsl = (hex) => {
  const r=parseInt(hex.slice(1,3),16)/255, g=parseInt(hex.slice(3,5),16)/255, b=parseInt(hex.slice(5,7),16)/255;
  const max=Math.max(r,g,b), min=Math.min(r,g,b); let h,s; const l=(max+min)/2;
  if(max===min){h=s=0;}
  else{const d=max-min; s=l>0.5?d/(2-max-min):d/(max+min);
    if(max===r)      h=((g-b)/d+(g<b?6:0))/6;
    else if(max===g) h=((b-r)/d+2)/6;
    else             h=((r-g)/d+4)/6;
  }
  return [Math.round(h*360), Math.round(s*100)];
};

/* ── icons ────────────────────────────────────────────────────────────────── */
const IconSun = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);
const IconMoon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);
const IconBell = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

/* ── ThemeSwitcher ────────────────────────────────────────────────────────── */
function ThemeSwitcher({ themeColor, setThemeColor, isDark }) {
  const content = (
    <div style={{ padding: "2px 0" }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", margin: "0 0 14px", color: "#aeaeb2" }}>
        Accent color
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", maxWidth: 208 }}>
        {THEMES.map((t) => {
          const active = themeColor === t.color;
          return (
            <button key={t.color} title={t.name} onClick={() => setThemeColor(t.color)}
              style={{
                width: 28, height: 28, borderRadius: "50%", background: t.color, border: "none", padding: 0,
                outline: active ? `2.5px solid ${t.color}` : "2.5px solid transparent", outlineOffset: 2,
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                transition: "transform 0.15s",
                boxShadow: active ? `0 0 0 5px ${tint(t.color,0.2)}, 0 2px 8px ${tint(t.color,0.5)}` : "0 1px 4px rgba(0,0,0,0.2)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.18)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              {active && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="bottomRight"
      overlayInnerStyle={{ borderRadius: 14, padding: "16px 18px", minWidth: 228 }}>
      <button title="Change accent color"
        style={{
          width: 34, height: 34, borderRadius: 8, background: "none", border: "none",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.15s", flexShrink: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
      >
        <div style={{ position: "relative", width: 20, height: 20 }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2.5px solid ${themeColor}`, opacity: 0.45 }} />
          <div style={{ position: "absolute", inset: 4, borderRadius: "50%", background: themeColor }} />
        </div>
      </button>
    </Popover>
  );
}

/* ── Icon button ──────────────────────────────────────────────────────────── */
function IconBtn({ children, onClick, title, isDark, hoverBgOverride, style = {} }) {
  const [h, setH] = useState(false);
  const hoverBg = hoverBgOverride ?? (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)");
  return (
    <button title={title} onClick={onClick}
      style={{ width: 32, height: 32, borderRadius: 8, background: h ? hoverBg : "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s", flexShrink: 0, verticalAlign: "middle", padding: 0, ...style }}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>
      {children}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   MAIN
═════════════════════════════════════════════════════════════════════════════*/
export default function PostLoginShell() {
  const { addNewTab, setActiveTab, openTabs } = useTabsSystemStore();
  const { themeColor, setThemeColor, isDark, toggleDark } = useThemeStore();

  const [menuItems,          setMenuItems]          = useState([]);
  const [menuLoading,        setMenuLoading]        = useState(true);
  const [openKeys,           setOpenKeys]           = useState([]);
  const [selectedComponent,  setSelectedComponent]  = useState("");
  const [userDetail,         setUserDeatils]        = useState({});
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openLogoutPopup,    setOpenLogoutPopup]    = useState(false);
  const [collapsed,          setCollapsed]          = useState(false);
  const [isMobile,           setIsMobile]           = useState(false);
  const [mobileOpen,         setMobileOpen]         = useState(false);
  const [searchOpen,         setSearchOpen]         = useState(false);
  const [searchValue,        setSearchValue]        = useState("");
  const [breadcrumb,         setBreadcrumb]         = useState([]);

  const SB_W     = 248;
  const SB_W_COL = 64;
  const sbWidth  = collapsed && !isMobile ? SB_W_COL : SB_W;

  /* ── design tokens ──────────────────────────────────────────────────────── */
  const D = useMemo(() => ({
    sbBg:       isDark ? "#1c1c21" : `linear-gradient(180deg, #ffffff 0%, ${tint(themeColor, 0.06)} 100%)`,
    sbDivider:  isDark ? "rgba(255,255,255,0.08)" : tint(themeColor, 0.14),
    sbTextMute: isDark ? "rgba(255,255,255,0.30)" : "#b0b3bb",

    hdrBg: isDark
      ? "#1c1c21"
      : `linear-gradient(90deg, #ffffff 0%, ${tint(themeColor,0.03)} 55%, ${tint(themeColor,0.07)} 100%)`,
    hdrShadow: isDark
      ? "0 1px 0 rgba(255,255,255,0.06), 0 4px 20px rgba(0,0,0,0.5)"
      : `0 1px 0 ${tint(themeColor,0.18)}, 0 4px 20px ${tint(themeColor,0.08)}`,
    pageBg:    isDark ? "#0f0f11"  : "#eef0f3",
    divider:   isDark ? "rgba(255,255,255,0.07)" : `${tint(themeColor,0.15)}`,
    textPri:   isDark ? "#f4f4f5"  : "#111827",
    textMute:  isDark ? "#a1a1aa"  : "#9ca3af",
    iconClr:   isDark ? "#a1a1aa"  : "#6b7280",
    hoverBg:   isDark ? "rgba(255,255,255,0.07)" : tint(themeColor,0.07),
    ctrlBg:    isDark ? "rgba(255,255,255,0.05)" : tint(themeColor,0.05),
    ctrlBorder:isDark ? "rgba(255,255,255,0.08)" : tint(themeColor,0.18),
  }), [isDark, themeColor]);

  /* ── antd menu tokens ───────────────────────────────────────────────────── */
  /* sidebar is always dark; active item gets the full theme-color accent */
  const menuCfg = useMemo(() => ({
    components: {
      Menu: {
        colorPrimary:       themeColor,
        itemBg:             "transparent",
        subMenuItemBg:      "#f7f8fa",
        popupBg:            "#ffffff",
        itemColor:          "#4b5563",
        itemHoverColor:     "#111827",
        itemHoverBg:        "#f3f4f6",
        itemSelectedBg:     tint(themeColor, 0.08),
        itemSelectedColor:  themeColor,
        itemBorderRadius:   8,
        itemMarginInline:   6,
        itemPaddingInline:  12,
        itemHeight:         38,
        fontSize:           13.5,
      },
    },
  }), [themeColor]);

  useEffect(() => {
    document.documentElement.style.setProperty("--ap",     themeColor);
    document.documentElement.style.setProperty("--ap-t10", tint(themeColor, 0.10));
    document.documentElement.style.setProperty("--ap-t20", tint(themeColor, 0.20));
  }, [themeColor]);

  useEffect(() => {
    setUserDeatils(JSON.parse(getCookie("userDetail")));
    loadMenuData();
  }, []);

  useEffect(() => {
    if (!menuItems?.length) return;
    const vm = menuItems.find((i) => i.key === "menu_18");
    if (vm?.children?.length === 1 && vm.children[0].key === "form_34") {
      onMenuSelect({ key: "form_34" });
      setOpenKeys(["menu_18"]);
    }
  }, [menuItems]);

  useEffect(() => {
    const onResize = () => { const m = window.innerWidth < 768; setIsMobile(m); if (!m) setMobileOpen(false); };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadMenuData = async () => {
    try {
      setMenuLoading(true);
      const items = await fetchDynamicMenuData(APIURL);
      if (items?.length) setMenuItems(items);
      else message.warning("No menu items found");
    } catch { message.error("Failed to load menu. Please refresh."); }
    finally { setMenuLoading(false); }
  };

  const flatLeaves = useMemo(() => {
    const flat = (items) => items.flatMap((i) => i.children?.length ? flat(i.children) : [i]);
    return flat(menuItems);
  }, [menuItems]);

  const searchOptions = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return [];
    return flatLeaves.filter((i) => i.label?.toLowerCase().includes(q)).slice(0, 10).map((i) => ({
      value: i.key,
      label: <div style={{ fontSize: 13, fontWeight: 500, color: "#1d1d1f" }}>{i.label}</div>,
    }));
  }, [searchValue, flatLeaves]);

  const findPath = (items, targetKey, trail = []) => {
    for (const item of items) {
      const cur = [...trail, item.label];
      if (item.key === targetKey) return cur;
      if (item.children) { const f = findPath(item.children, targetKey, cur); if (f) return f; }
    }
    return null;
  };

  const onMenuSelect = ({ key }) => {
    const item = flatLeaves.find((i) => i.key === key);
    if (item) {
      const alreadyOpen = openTabs.some((t) => t.key === key);
      if (!alreadyOpen) {
        addNewTab([...openTabs, { closable: true, key: item.key, label: item.label, icon: item.icon }]);
      }
    }
    /* expand parent submenu so the active item is always visible */
    const parent = menuItems.find((m) => m.children?.some((c) => c.key === key));
    if (parent) setOpenKeys((prev) => prev.includes(parent.key) ? prev : [...prev, parent.key]);

    setSelectedComponent(key);
    setActiveTab(key);
    const path = findPath(menuItems, key);
    setBreadcrumb(path ?? []);
    if (isMobile) setMobileOpen(false);
  };

  const handleProfileMenuClick = ({ key }) => {
    if (key === "logout")          setOpenLogoutPopup(true);
    if (key === "change-password") setOpenChangePassword(true);
  };

  const handleOk = async () => {
    setOpenLogoutPopup(false);
    const val = await apiRequest("POST", "Users/logout");
    if (val?.IsSuccess) {
      await new Promise((r) => setTimeout(r, 250));
      deleteCookie("auth"); deleteCookie("userDetail"); deleteCookie("token");
      window.location.href = "/"; localStorage.clear();
    }
  };

  const initials = userDetail?.UserName
    ? userDetail.UserName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "U";

  /* ── Sidebar ──────────────────────────────────────────────────────────── */
  const sidebarPanel = (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: D.sbBg, borderRight: `1px solid ${D.sbDivider}` }}>

      {/* ── Logo ──────────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        justifyContent: collapsed && !isMobile ? "center" : "flex-start",
        padding: collapsed && !isMobile ? "18px 0" : "14px 16px",
        borderBottom: `1px solid ${D.sbDivider}`,
        background: `linear-gradient(135deg, ${tint(themeColor, 0.09)} 0%, transparent 70%)`,
        height: 56, flexShrink: 0, transition: "background 0.4s",
      }}>
        <div style={{
          width: 34, height: 34, flexShrink: 0,
          background: "#ffffff", borderRadius: 9,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 4,
          border: `1.5px solid ${tint(themeColor, 0.30)}`,
          boxShadow: `0 2px 8px ${tint(themeColor, 0.20)}`,
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}>
          <img src={companyLogo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
        {!collapsed && !isMobile && (
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827", whiteSpace: "nowrap", letterSpacing: "-0.2px" }}>
              Vendor Portal
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 10.5, color: themeColor, fontWeight: 600, whiteSpace: "nowrap", letterSpacing: "0.02em", transition: "color 0.3s" }}>
              Hypermax City
            </p>
          </div>
        )}
      </div>

      {/* ── Menu ──────────────────────────────────────────────────────────── */}
      <div className="sb-scr" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "10px 0" }}>
        {!collapsed && !isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 16px 8px", margin: 0 }}>
            <div style={{ width: 12, height: 1.5, borderRadius: 2, background: tint(themeColor, 0.50) }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: D.sbTextMute, letterSpacing: "0.10em", textTransform: "uppercase" }}>Menu</span>
          </div>
        )}
        {menuLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 140 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 20, color: themeColor }} spin />} />
          </div>
        ) : (
          <ConfigProvider theme={menuCfg}>
            <Menu
              className="sb-light"
              theme="light"
              mode="inline"
              selectedKeys={[selectedComponent]}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              onSelect={onMenuSelect}
              items={menuItems}
              inlineCollapsed={collapsed && !isMobile}
              style={{ background: "transparent", border: "none", fontWeight: 500 }}
            />
          </ConfigProvider>
        )}
      </div>

      {/* ── User ──────────────────────────────────────────────────────────── */}
      <div style={{ padding: collapsed && !isMobile ? "10px 0" : "8px 10px", flexShrink: 0, borderTop: `1px solid ${D.sbDivider}` }}>
        <Dropdown menu={{ items: profileMenuItems, onClick: handleProfileMenuClick }} placement="topRight" trigger={["click"]}>
          <div
            style={{
              display: "flex", alignItems: "center", gap: 9,
              padding: collapsed && !isMobile ? "7px 0" : "7px 8px",
              borderRadius: 8, cursor: "pointer",
              justifyContent: collapsed && !isMobile ? "center" : "flex-start",
              transition: "background 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = tint(themeColor, 0.08); }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            <Avatar size={30} style={{
              background: themeColor, color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0,
              boxShadow: `0 0 0 2px #fff, 0 0 0 3.5px ${tint(themeColor, 0.35)}`,
              transition: "background 0.3s, box-shadow 0.3s",
            }}>
              {initials}
            </Avatar>
            {!collapsed && !isMobile && (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", lineHeight: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {userDetail?.UserName ?? "Admin User"}
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", lineHeight: "15px", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {userDetail?.Email ?? ""}
                  </div>
                </div>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={tint(themeColor, 0.50)} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════════════════════
     RENDER — standard full-screen layout
  ═════════════════════════════════════════════════════════════════════════*/
  return (
    <div style={{
      position: "fixed", inset: 0,
      display: "flex",
      background: D.pageBg,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      WebkitFontSmoothing: "antialiased",
      transition: "background 0.3s",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        /* light sidebar */
        .sb-light .ant-menu-item-selected::after { display: none !important; }
        .sb-light .ant-menu-item-selected {
          font-weight: 600 !important;
          box-shadow: inset 3px 0 0 var(--ap) !important;
        }
        .sb-light .ant-menu-item-selected .anticon,
        .sb-light .ant-menu-item-selected .ant-menu-item-icon { color: var(--ap) !important; }
        .sb-light .ant-menu-submenu-open > .ant-menu-submenu-title { color: var(--ap) !important; font-weight: 600 !important; }
        .sb-light .ant-menu-submenu-open > .ant-menu-submenu-title .anticon,
        .sb-light .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-item-icon,
        .sb-light .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-submenu-arrow { color: var(--ap) !important; }
        .sb-light .ant-menu-sub.ant-menu-inline { background: #f7f8fa !important; }
        .sb-light .ant-menu-item { transition: background 0.15s, color 0.15s !important; }

        .sb-scr::-webkit-scrollbar { width: 3px; }
        .sb-scr::-webkit-scrollbar-track { background: transparent; }
        .sb-scr::-webkit-scrollbar-thumb { background: var(--ap-t20); border-radius: 4px; }
        .sb-scr::-webkit-scrollbar-thumb:hover { background: var(--ap-t20); }

        .con-scr::-webkit-scrollbar { width: 4px; }
        .con-scr::-webkit-scrollbar-track { background: transparent; }
        .con-scr::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.15); border-radius: 4px; }
      `}</style>

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      {!isMobile && (
        <div style={{
          width: sbWidth, flexShrink: 0,
          borderRight: "none",
          transition: "width 0.25s cubic-bezier(0.4,0,0.2,1), border-color 0.3s",
          overflow: "hidden",
        }}>
          {sidebarPanel}
        </div>
      )}

      {/* ── Right column ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>

        {/* Header */}
        <div style={{
          height: 56, flexShrink: 0,
          background: D.hdrBg,
          boxShadow: D.hdrShadow,
          display: "flex", alignItems: "center",
          padding: isMobile ? "0 16px" : "0 20px",
          gap: 12, zIndex: 10,
          position: "relative", overflow: "hidden",
          transition: "background 0.3s, box-shadow 0.3s",
        }}>
          {/* right-side glow bloom */}
          {!isDark && (
            <div style={{
              position: "absolute", right: -30, top: "50%",
              transform: "translateY(-50%)",
              width: 220, height: 220, borderRadius: "50%",
              background: tint(themeColor, 0.18),
              filter: "blur(50px)", pointerEvents: "none",
              transition: "background 0.4s",
            }} />
          )}

          {/* Left: collapse toggle + breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, height: "100%" }}>
            <IconBtn
              isDark={isDark}
              title={collapsed ? "Expand" : "Collapse"}
              onClick={() => isMobile ? setMobileOpen(true) : setCollapsed(!collapsed)}
              style={{ color: D.iconClr, flexShrink: 0 }}
            >
              {collapsed || isMobile
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><polyline points="11 8 13 10 11 12"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><polyline points="14 8 12 10 14 12"/></svg>
              }
            </IconBtn>

            {/* thin vertical divider */}
            {!isMobile && (
              <div style={{ width: 1, height: 16, background: D.divider, flexShrink: 0 }} />
            )}

            {!isMobile && (
              <Breadcrumb
                items={[
                  { title: <HomeOutlined style={{ fontSize: 12, color: D.textMute, verticalAlign: "middle" }} /> },
                  ...breadcrumb.map((label, i) => ({
                    title: (
                      <span style={{
                        fontSize: 12,
                        color: i === breadcrumb.length - 1 ? themeColor : D.textMute,
                        fontWeight: i === breadcrumb.length - 1 ? 600 : 400,
                        lineHeight: "22px",
                      }}>
                        {label}
                      </span>
                    ),
                  })),
                ]}
                style={{ userSelect: "none", margin: 0, lineHeight: "22px" }}
              />
            )}
          </div>

          {/* Right: controls */}
          <div style={{
            display: "flex", alignItems: "center", gap: 4,
            background: D.ctrlBg,
            border: `1px solid ${D.ctrlBorder}`,
            borderRadius: 12, padding: "4px 8px",
            backdropFilter: "blur(8px)",
            position: "relative", zIndex: 1,
            transition: "background 0.3s, border-color 0.3s",
          }}>
            {/* Search */}
            <Popover open={searchOpen} onOpenChange={setSearchOpen} trigger="click" placement="bottomRight"
              overlayInnerStyle={{ borderRadius: 14, padding: "12px", minWidth: 280 }}
              content={
                <AutoComplete options={searchOptions} value={searchValue} onChange={setSearchValue} style={{ width: "100%" }}
                  onSelect={(key) => { onMenuSelect({ key }); setSearchOpen(false); setSearchValue(""); }}>
                  <Input prefix={<span style={{ color: D.iconClr, display: "flex" }}><IconSearch /></span>}
                    placeholder="Search modules…" size="middle" allowClear autoFocus style={{ borderRadius: 8 }} />
                </AutoComplete>
              }>
              <IconBtn isDark={isDark} title="Search" style={{ color: themeColor }}>
                <IconSearch />
              </IconBtn>
            </Popover>

            <div style={{ width: 1, height: 16, background: D.ctrlBorder, flexShrink: 0 }} />

            <IconBtn isDark={isDark} title={isDark ? "Light mode" : "Dark mode"} onClick={toggleDark} style={{ color: D.iconClr }}>
              {isDark ? <IconSun /> : <IconMoon />}
            </IconBtn>

            <ThemeSwitcher themeColor={themeColor} setThemeColor={setThemeColor} isDark={isDark} />

            <IconBtn isDark={isDark} title="Notifications" style={{ color: D.iconClr }}>
              <IconBell />
            </IconBtn>

            <div style={{ width: 1, height: 16, background: D.ctrlBorder, flexShrink: 0 }} />

            <Dropdown menu={{ items: profileMenuItems, onClick: handleProfileMenuClick }} trigger={["click"]} placement="bottomRight">
              <Avatar
                size={30}
                style={{
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}cc 100%)`,
                  fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0,
                  boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${tint(themeColor,0.45)}`,
                  transition: "box-shadow 0.3s, background 0.3s",
                }}
              >
                {initials}
              </Avatar>
            </Dropdown>
          </div>
        </div>

        {/* Content */}
        <div className="con-scr" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: D.pageBg, transition: "background 0.3s" }}>
          <DynamicTabSystem
            selectedMenuItem={selectedComponent}
            menuConfig={menuConfig}
            onTabClose={() => setSelectedComponent("")}
            onMenuSelect={(key) => onMenuSelect({ key })}
          />
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, display: "flex" }}>
          <div onClick={() => setMobileOpen(false)} style={{ flex: 1, background: "rgba(0,0,0,0.35)" }} />
          <div style={{ width: 256, background: D.sbBg, overflow: "hidden", boxShadow: "4px 0 24px rgba(0,0,0,0.12)" }}>
            {sidebarPanel}
          </div>
        </div>
      )}

      {/* Logout */}
      <Modal open={openLogoutPopup} title="Sign out" onCancel={() => setOpenLogoutPopup(false)}
        footer={[
          <Button key="c" onClick={() => setOpenLogoutPopup(false)}>Cancel</Button>,
          <Button key="l" type="primary" danger onClick={handleOk}>Sign out</Button>,
        ]}>
        <p style={{ fontSize: 14 }}>Are you sure you want to sign out?</p>
      </Modal>

      {openChangePassword && <ChangePasswordModal open={openChangePassword} onCancel={() => setOpenChangePassword(false)} />}
    </div>
  );
}
